import type { ScheduleDay } from '../schedule';

export type ScenarioType =
  | 'slot'
  | 'notification'
  | 'dispensarization'
  | 'form';

export interface ScenarioValues {
  readonly callsMaxTries: number;
  readonly callsIntervalMinutes: number;
  readonly callsDisable: boolean;
}

export interface ScenarioScheduleValues {
  readonly callsScheduleId: string | null;
  /** null means the schedule body has not been loaded. */
  readonly callSchedule: ScheduleDay[] | null;
  readonly callHolidays: string[] | null;
}

export type ScenarioCacheEntry = ScenarioValues & ScenarioScheduleValues;
export type ScenarioCacheValues = ScenarioValues &
  Partial<ScenarioScheduleValues>;
export type ServerScenarioValues = ScenarioValues &
  Pick<ScenarioScheduleValues, 'callsScheduleId'> &
  Partial<ScenarioScheduleValues>;
export interface CacheHit extends ScenarioScheduleValues {
  readonly settingsLoaded: boolean;
}

export interface DirtyScenario extends ScenarioCacheEntry {
  readonly type: ScenarioType;
  readonly isScheduleDirty: boolean;
  readonly isFieldsDirty: boolean;
}

export const SCENARIO_TYPES: readonly ScenarioType[] = [
  'slot',
  'notification',
  'dispensarization',
  'form',
];

export const DEFAULT_SCENARIO_VALUES: ScenarioValues = {
  callsMaxTries: 3,
  callsIntervalMinutes: 60,
  callsDisable: false,
};

export const normalizeCallsDisable = (
  value: boolean | null | undefined,
): boolean => value === true;

export const createScenarioEntry = (
  values: ScenarioCacheValues,
  previous?: ScenarioCacheEntry,
): ScenarioCacheEntry => ({
  callsMaxTries: values.callsMaxTries,
  callsIntervalMinutes: values.callsIntervalMinutes,
  callsDisable: values.callsDisable,
  callsScheduleId:
    values.callsScheduleId !== undefined
      ? values.callsScheduleId
      : (previous?.callsScheduleId ?? null),
  callSchedule:
    values.callSchedule !== undefined
      ? (values.callSchedule?.map((day) => ({ ...day })) ?? null)
      : (previous?.callSchedule ?? null),
  callHolidays:
    values.callHolidays !== undefined
      ? values.callHolidays
        ? [...values.callHolidays]
        : null
      : (previous?.callHolidays ?? null),
});

const isScheduleEqual = (
  a: ScheduleDay[] | null,
  b: ScheduleDay[] | null,
): boolean => {
  if (a === null || b === null) return a === b;
  if (a.length !== b.length) return false;
  return a.every((day, i) => day.start === b[i].start && day.end === b[i].end);
};

const isHolidaysEqual = (a: string[] | null, b: string[] | null): boolean => {
  if (a === null || b === null) return a === b;
  if (a.length !== b.length) return false;
  return a.every((holiday, i) => holiday === b[i]);
};

export const getDirtyScenario = (
  type: ScenarioType,
  current: ScenarioCacheEntry,
  server: ScenarioCacheEntry,
): DirtyScenario | null => {
  const isFieldsDirty =
    current.callsMaxTries !== server.callsMaxTries ||
    current.callsIntervalMinutes !== server.callsIntervalMinutes ||
    current.callsDisable !== server.callsDisable;
  const scheduleLoaded =
    server.callSchedule !== null && server.callHolidays !== null;
  const isScheduleDirty =
    scheduleLoaded &&
    (!isScheduleEqual(current.callSchedule, server.callSchedule) ||
      !isHolidaysEqual(current.callHolidays, server.callHolidays));
  return isFieldsDirty || isScheduleDirty
    ? {
        ...current,
        callsScheduleId: scheduleLoaded
          ? current.callsScheduleId
          : server.callsScheduleId,
        callSchedule: scheduleLoaded ? current.callSchedule : null,
        callHolidays: scheduleLoaded ? current.callHolidays : null,
        type,
        isFieldsDirty,
        isScheduleDirty,
      }
    : null;
};
